import { useState, useEffect, useRef } from "react";
import i18n from "../../utils/i18n";
import Meals from "./Meals";
import RadioButton from "../RadioButton";
import DateAndTimeInput from "./DateAndTimeInput";
import { calculateMeals } from "../../utils/calculateMeals";
import { getDateTime } from "../../utils/getDateTime";
import {
  getDateTimeFromPicker,
  getTimeFromInput,
} from "../../utils/dateTimeConverters";

const ShiftForm = ({ shift, event, onSubmitHandler, onDeleteHandler }) => {
  const formRef = useRef(null);
  const startTimeInputRef = useRef(null);
  const endTimeInputRef = useRef(null);

  const [attendanceLimit, setAttendanceLimit] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const [dayEvening, setDayEvening] = useState(null);
  const [beforeAfter, setBeforeAfter] = useState(null);
  const [meetingPoint, setMeetingPoint] = useState("");
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [lunch, setLunch] = useState(null);
  const [dinner, setDinner] = useState(null);

  // Initialize state from shift prop
  useEffect(() => {
    if (shift) {
      setStartDate(shift.start_time);
      setStartTime(shift.start_time);
      setEndDate(shift.end_time);
      setEndTime(shift.end_time);
      setLunch(shift.lunch);
      setDinner(shift.dinner);
      setAttendanceLimit(shift.attendance_limit);
      setMeetingPoint(shift.meeting_point || "");
      setName(shift.name || "");
      setDayEvening(shift.day_evening);
      setBeforeAfter(shift.before_after);
    }
  }, [shift]);

  // Initialize inputmask on mount. Inputmask's own API rather than its jQuery plugin, so this
  // component does not need jQuery from the Sprockets bundle to mask its time fields.
  useEffect(() => {
    if (!formRef.current) return;

    // Inputmask's own API rather than its jQuery plugin, and one instance per field (an instance
    // holds per-element state). `window.Inputmask` is the copy the Sprockets bundle already loads:
    // importing the npm package here instead puts a second copy on the page, and the two of them
    // leave the field masked but inert.
    formRef.current.querySelectorAll(".time_input").forEach((input) => {
      window.Inputmask({
        alias: "datetime",
        inputFormat: "HH:mm",
        placeholder: i18n.t("schedule.shifts.form.inputmask_placeholder"),
        showMaskOnHover: false,
      }).mask(input);
    });
  }, []);

  // Calculate meals when dates/times change
  useEffect(() => {
    const state = {
      startDate,
      startTime,
      endDate,
      endTime,
    };
    const startDateTime = getDateTime("start", state);
    const endDateTime = getDateTime("end", state);

    if (startDateTime && endDateTime) {
      const meals = calculateMeals(startDateTime, endDateTime);
      setLunch(meals.lunch);
      setDinner(meals.dinner);
    }
  }, [startDate, startTime, endDate, endTime]);

  const validate = () => {
    setValidationError(null);

    if (!attendanceLimit) {
      setValidationError(
        i18n.t("activemodel.errors.models.shift.attendance_limit_empty")
      );
      return false;
    }

    if (!meetingPoint) {
      setValidationError(
        i18n.t("activemodel.errors.models.shift.meeting_point_empty")
      );
      return false;
    }

    const state = { startDate, startTime, endDate, endTime };
    const startDateTime = getDateTime("start", state);
    const endDateTime = getDateTime("end", state);

    if (
      !startDateTime ||
      !startDateTime.isValid ||
      !endDateTime ||
      !endDateTime.isValid
    ) {
      setValidationError(
        i18n.t("activemodel.errors.models.shift.invalid_date_and_time")
      );
      return false;
    }

    if (endDateTime < startDateTime) {
      setValidationError(
        i18n.t("activemodel.errors.models.shift.end_time_before_start_time")
      );
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      const state = { startDate, startTime, endDate, endTime };
      onSubmitHandler({
        start_time: getDateTime("start", state),
        end_time: getDateTime("end", state),
        lunch: lunch,
        dinner: dinner,
        attendance_limit: attendanceLimit,
        meeting_point: meetingPoint,
        name: name,
        day_evening: dayEvening,
        before_after: beforeAfter,
      });
      clearForm();
    }
  };

  const clearForm = () => {
    setAttendanceLimit(0);
    setValidationError(null);
    setDayEvening(null);
    setBeforeAfter(null);
    setMeetingPoint("");
    setName("");
    setStartDate(null);
    setStartTime(null);
    setEndDate(null);
    setEndTime(null);
    setLunch(null);
    setDinner(null);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    onDeleteHandler();
  };

  const startDateHandleChange = (e) => {
    const dateTime = getDateTimeFromPicker(e.date);
    if (!dateTime) return;

    setStartDate(dateTime);
    setEndDate(dateTime);
    startTimeInputRef.current?.focus();
  };

  const startTimeHandleChange = (e) => {
    const parsedTime = getTimeFromInput(e.target.value);
    setStartTime(parsedTime);
  };

  const endDateHandleChange = (e) => {
    const dateTime = getDateTimeFromPicker(e.date);
    if (!dateTime) return;

    setStartDate((previous) => previous || dateTime);
    setEndDate(dateTime);
    endTimeInputRef.current?.focus();
  };

  const endTimeHandleChange = (e) => {
    const parsedTime = getTimeFromInput(e.target.value);
    setEndTime(parsedTime);
  };

  const handleDayEveningChange = (value) => {
    if (value === "neither") {
      value = null;
    }
    setDayEvening(value);
  };

  const handleBeforeAfterChange = (value) => {
    if (value === "neither") {
      value = null;
    }
    setBeforeAfter(value);
  };

  const dayEveningInput = (value, checked) => {
    return (
      <RadioButton
        label={i18n.t("schedule.shifts.form.day_evening_labels." + value)}
        value={value}
        checked={checked}
        onChange={handleDayEveningChange}
      />
    );
  };

  const beforeAfterInput = (value, checked) => {
    return (
      <RadioButton
        label={i18n.t("schedule.shifts.form.before_after_labels." + value)}
        value={value}
        checked={checked}
        onChange={handleBeforeAfterChange}
      />
    );
  };

  const deleteButton = shift ? (
    <a href="##" className="btn btn-danger" onClick={handleDelete}>
      {i18n.t("schedule.shifts.form.delete")}
    </a>
  ) : null;

  const alert = validationError ? (
    <div className="alert alert-danger">{validationError}</div>
  ) : null;

  return (
    <form ref={formRef} className="form" onSubmit={handleSubmit}>
      <div className="modal-body">
        {alert}
        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.name")}</label>
          <input
            type="text"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-control"
            placeholder={i18n.t("schedule.shifts.form.name_placeholder")}
          />
        </div>

        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.from")}</label>
          <DateAndTimeInput
            type="start"
            selectedDate={startDate}
            selectedTime={startTime}
            event={event}
            onDateChange={startDateHandleChange}
            onTimeBlur={startTimeHandleChange}
            timeInputRef={startTimeInputRef}
          />
        </div>

        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.to")}</label>
          <DateAndTimeInput
            type="end"
            selectedDate={endDate}
            selectedTime={endTime}
            event={event}
            onDateChange={endDateHandleChange}
            onTimeBlur={endTimeHandleChange}
            timeInputRef={endTimeInputRef}
          />
        </div>

        <Meals lunch={lunch} dinner={dinner} />

        <div className="form-group radio_buttons optional">
          <label className="radio_buttons optional control-label">
            {i18n.t("schedule.shifts.form.day_evening")}
          </label>
          {dayEveningInput("neither", dayEvening === null)}
          {dayEveningInput("day", dayEvening === "day")}
          {dayEveningInput("evening", dayEvening === "evening")}
          <span className="help-block">
            {i18n.t("schedule.shifts.form.day_evening_help", {
              day: 1,
              evening: 1,
            })}
          </span>
        </div>

        <div className="form-group radio_buttons optional">
          <label className="radio_buttons optional control-label">
            {i18n.t("schedule.shifts.form.before_after")}
          </label>
          {beforeAfterInput("neither", beforeAfter === null)}
          {beforeAfterInput("before", beforeAfter === "before")}
          {beforeAfterInput("after", beforeAfter === "after")}
          <span className="help-block">
            {i18n.t("schedule.shifts.form.before_after_help", {
              before: 1,
              after: 1,
            })}
          </span>
        </div>

        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.member_limit")}</label>
          <input
            type="integer"
            name="attendance_limit"
            required
            value={attendanceLimit}
            onChange={(e) => setAttendanceLimit(e.target.value.trim())}
            className="form-control"
            placeholder={i18n.t(
              "schedule.shifts.form.member_limit_placeholder"
            )}
          />
        </div>

        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.meeting_point")}</label>
          <input
            type="text"
            name="meeting_point"
            required
            value={meetingPoint}
            onChange={(e) => setMeetingPoint(e.target.value)}
            className="form-control"
            placeholder={i18n.t(
              "schedule.shifts.form.meeting_point_placeholder"
            )}
          />
        </div>
      </div>
      <div className="modal-footer">
        <div className="pull-left">{deleteButton}</div>
        <input
          type="submit"
          className="btn btn-primary"
          value={
            shift
              ? i18n.t("schedule.shifts.form.save")
              : i18n.t("schedule.shifts.form.create")
          }
        />
      </div>
    </form>
  );
};

export default ShiftForm;
